/**
 * CompanySetupHub — New landing page at `/control/company-setup`
 *
 * Replaces the old direct-emirates grid with 2 top-level cards
 * (iter-220, Feb 2026):
 *   1. قاعدة بيانات الرخص التجارية و الأوراق
 *      → drills into country selector → emirate grid.
 *   2. قائمة مدخلات تسجيل الشركات
 *      → CRUD for the cascading dropdowns (country / region / authority).
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Database, ListTree } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { usePerms } from '../permissions/PermissionsContext';

const CARDS = [
  {
    id: 'licenses-db',
    icon: Database,
    color: '#06B6D4',
    name_ar: 'قاعدة بيانات الرخص التجارية و الأوراق',
    name_en: 'Trade Licenses & Documents Database',
    desc_ar: 'اختر الدولة لعرض الجهات وقواعد بيانات الرخص',
    desc_en: 'Pick a country to browse authorities and licensing databases',
    route: '/control/company-setup/database',
  },
  {
    id: 'inputs-list',
    icon: ListTree,
    color: '#F5A55A',
    name_ar: 'قائمة مدخلات تسجيل الشركات',
    name_en: 'Company Registration Inputs',
    desc_ar: 'تحكم في الدول والإمارات والجهات المستخدمة في فورم التسجيل',
    desc_en: 'Manage countries, regions and authorities used in the registration form',
    route: '/control/company-setup/inputs',
  },
];

export default function CompanySetupHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-indigo-500/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton={true} backPath="/control/cloudgm" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
            { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'تأسيس الشركات', label_en: 'Company Setup', path: null }
          ]} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-white mb-2">
          {isAr ? 'تأسيس الشركات' : 'Company Setup'}
        </h1>
        <p className="text-[#71717A]">
          {isAr ? 'اختر أحد المدخلين أدناه' : 'Pick one of the modules below'}
        </p>
      </div>

      <main className="relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {visiblePages(CARDS, 'route').map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.button
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  onClick={() => navigate(c.route)}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#151530] to-[#0f0f24] p-8 text-start hover:border-white/25 transition-colors"
                  data-testid={`company-setup-card-${c.id}`}
                >
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: `radial-gradient(circle at top right, ${c.color}22, transparent 60%)` }}
                  />
                  <div className="relative">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                      style={{ backgroundColor: `${c.color}20`, border: `1px solid ${c.color}44` }}
                    >
                      <Icon className="h-7 w-7" style={{ color: c.color }} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {isAr ? c.name_ar : c.name_en}
                    </h3>
                    <p className="text-sm text-[#a0a0b8]">
                      {isAr ? c.desc_ar : c.desc_en}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
